import {
  createContext, useContext, useState, ReactNode,
  useMemo,
} from 'react';

interface FilterDrawerContextType {
  isOpen: boolean;
  toggleDrawer: () => void;
  openDrawer: () => void;
  closeDrawer: () => void;
  activeSection: string | null;
  setActiveSection: (section: string | null) => void;
}

export const FilterDrawerContext = createContext<FilterDrawerContextType | undefined>(undefined);

interface FilterDrawerProviderProps {
  children: ReactNode;
}

/**
 * Provider for filter drawer state management
 */
export const FilterDrawerProvider = ({ children }: FilterDrawerProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSectionState] = useState<string | null>(null);

  const toggleDrawer = () => setIsOpen((prev) => {
    const next = !prev;
    if (!next) setActiveSectionState(null);
    return next;
  });
  const openDrawer = () => setIsOpen(true);
  const closeDrawer = () => {
    setIsOpen(false);
    setActiveSectionState(null);
  };

  const setActiveSection = (section: string | null) => {
    setActiveSectionState(section);
  };

  const value = useMemo(() => ({
    isOpen,
    toggleDrawer,
    openDrawer,
    closeDrawer,
    activeSection,
    setActiveSection,
  }), [isOpen, activeSection]);

  return (
    <FilterDrawerContext.Provider value={value}>
      {children}
    </FilterDrawerContext.Provider>
  );
};

/**
 * Hook to use the filter drawer context
 */
export const useFilterDrawer = () => {
  const context = useContext(FilterDrawerContext);
  if (context === undefined) {
    throw new Error('useFilterDrawer must be used within a FilterDrawerProvider');
  }
  return context;
};
